import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Check, X, Clock, AlertCircle, Star, ArrowLeft, Sparkles, Pencil, Trash2, RefreshCw, Code2, Copy, Layers } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PROD_URL = 'https://fairviews.com';

const AdminReviewsPage = () => {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [pendingReviews, setPendingReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [aiReviews, setAiReviews] = useState([]);
  const [aiTotal, setAiTotal] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editForm, setEditForm] = useState({ author: '', rating: 5, title: '', content: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [showSeoModal, setShowSeoModal] = useState(false);
  const [seoSnippet, setSeoSnippet] = useState('');
  const [seoLoading, setSeoLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [seoWidgetType, setSeoWidgetType] = useState('badge');
  const [productName, setProductName] = useState('');
  const [savingProduct, setSavingProduct] = useState(false);
  const [productSaved, setProductSaved] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkTarget, setBulkTarget] = useState('');
  const [bulkCurrent, setBulkCurrent] = useState(0);
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkStarting, setBulkStarting] = useState(false);
  const [bulkError, setBulkError] = useState('');

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (domain) {
      loadData();
    }
  }, [domain]);

  const getToken = () => localStorage.getItem('admin_token');

  const checkAuth = async () => {
    const token = getToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (err) {
      navigate('/admin/login');
    }
  };

  const loadData = async () => {
    const token = getToken();
    if (!token) return;

    try {
      setLoading(true);
      
      // Load pending reviews (requires auth)
      const pendingRes = await fetch(`${API_URL}/api/reviews/pending/${domain}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        setPendingReviews(pendingData.reviews || []);
      } else if (pendingRes.status === 401) {
        navigate('/admin/login');
        return;
      }
      
      // Load stats (public)
      const statsRes = await fetch(`${API_URL}/api/reviews/stats/${domain}`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      // Load AI-generated reviews (requires auth)
      const aiRes = await fetch(`${API_URL}/api/admin/reviews/${domain}?source=ai&limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (aiRes.ok) {
        const aiData = await aiRes.json();
        setAiReviews(aiData.reviews || []);
        setAiTotal(aiData.total || 0);
      }
      
    } catch (err) {
      setError('Erreur lors du chargement des données');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    const token = getToken();
    if (!token) { navigate('/admin/login'); return; }
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/generate/${domain}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await loadData();
      } else if (response.status === 401) {
        navigate('/admin/login');
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de la génération');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur de connexion');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    const token = getToken();
    if (!token) { navigate('/admin/login'); return; }
    if (!window.confirm('Supprimer définitivement cet avis ?')) return;
    setActionLoading(reviewId);
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        await loadData();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const openEdit = (review) => {
    setEditingReview(review);
    setEditForm({
      author: review.author || '',
      rating: review.rating || 5,
      title: review.title || '',
      content: review.content || ''
    });
  };

  const handleSaveEdit = async () => {
    const token = getToken();
    if (!token || !editingReview) return;
    setSavingEdit(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${editingReview.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editForm)
      });
      if (response.ok) {
        setEditingReview(null);
        await loadData();
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSavingEdit(false);
    }
  };

  const dynamicSnippet = `<!-- Widget FairViews + étoiles Google (mise à jour automatique) -->
<div data-fairviews-widget data-type="${seoWidgetType}" data-domain="${domain}" data-schema="product"></div>
<script src="${PROD_URL}/api/widget/script.js" async></script>`;

  const openSeoModal = async () => {
    setShowSeoModal(true);
    setSeoLoading(true);
    setSeoSnippet('');
    setProductSaved(false);
    // Prefill product name from company (product_name or category)
    try {
      const compRes = await fetch(`${API_URL}/api/companies/domain/${domain}`);
      if (compRes.ok) {
        const comp = await compRes.json();
        setProductName(comp.product_name || comp.category || '');
      }
    } catch (err) {
      // ignore
    }
    try {
      const res = await fetch(`${API_URL}/api/widget/schema-snippet/${domain}?type=product`);
      const text = await res.text();
      setSeoSnippet(text);
    } catch (err) {
      setSeoSnippet('<!-- Erreur de chargement du snippet -->');
    } finally {
      setSeoLoading(false);
    }
  };

  const refreshSnippet = async () => {
    try {
      const res = await fetch(`${API_URL}/api/widget/schema-snippet/${domain}?type=product`);
      setSeoSnippet(await res.text());
    } catch (err) {
      // ignore
    }
  };

  const handleSaveProductName = async () => {
    const token = getToken();
    if (!token || !productName.trim()) return;
    setSavingProduct(true);
    setProductSaved(false);
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/company/${domain}/product-name`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ product_name: productName.trim() })
      });
      if (res.ok) {
        setProductSaved(true);
        await refreshSnippet();
        setTimeout(() => setProductSaved(false), 2500);
      } else if (res.status === 401) {
        navigate('/admin/login');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSavingProduct(false);
    }
  };

  const openBulkModal = async () => {
    setShowBulkModal(true);
    setBulkError('');
    const token = getToken();
    try {
      const wRes = await fetch(`${API_URL}/api/widget/data/${domain}?limit=1`);
      const wData = await wRes.json();
      const cur = wData?.company?.total_reviews || 0;
      setBulkCurrent(cur);
      setBulkTarget(String(cur + 100));
    } catch (err) {
      // ignore
    }
    try {
      const sRes = await fetch(`${API_URL}/api/admin/reviews/bulk-status/${domain}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (sRes.ok) {
        const j = await sRes.json();
        // On ne reprend l'affichage que si une génération est réellement en cours
        setBulkJob(j.status === 'running' ? j : null);
      } else {
        setBulkJob(null);
      }
    } catch (err) {
      setBulkJob(null);
    }
  };

  const startBulk = async () => {
    const token = getToken();
    const target = parseInt(bulkTarget, 10);
    if (!token || !target) return;
    if (target <= bulkCurrent) {
      setBulkError(`La cible doit être supérieure au total actuel (${bulkCurrent}).`);
      return;
    }
    setBulkStarting(true);
    setBulkError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/reviews/bulk-generate/${domain}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ target })
      });
      const data = await res.json();
      if (res.ok) {
        setBulkJob({ status: 'running', created: 0, to_create: data.to_create, target, current_total: bulkCurrent });
      } else if (res.status === 401) {
        navigate('/admin/login');
      } else {
        setBulkError(data.detail || 'Erreur lors du démarrage');
      }
    } catch (err) {
      setBulkError('Erreur de connexion');
    } finally {
      setBulkStarting(false);
    }
  };

  useEffect(() => {
    if (!showBulkModal || bulkJob?.status !== 'running') return;
    const id = setInterval(async () => {
      const token = getToken();
      try {
        const res = await fetch(`${API_URL}/api/admin/reviews/bulk-status/${domain}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const j = await res.json();
          setBulkJob(j);
          if (j.status !== 'running') {
            clearInterval(id);
            if (j.status === 'done') loadData();
          }
        }
      } catch (err) {
        // ignore transient errors
      }
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBulkModal, bulkJob?.status, domain]);

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const handleModerate = async (reviewId, action) => {
    const token = getToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }

    setActionLoading(reviewId);
    
    try {
      const response = await fetch(`${API_URL}/api/reviews/moderate/${reviewId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action })
      });
      
      if (response.ok) {
        await loadData();
      } else if (response.status === 401) {
        navigate('/admin/login');
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de la modération');
      }
    } catch (err) {
      console.error(err);
      alert('Erreur de connexion');
    } finally {
      setActionLoading(null);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <div
            key={star}
            className={`w-5 h-5 flex items-center justify-center ${
              star <= rating ? 'bg-[#00b67a]' : 'bg-gray-200'
            }`}
          >
            <Star className="w-3 h-3 text-white fill-white" />
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link 
          to={`/review/${domain}`}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-[#00b67a] mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la page des avis
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg border p-6 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-[#1a1a1a] mb-2">
                Administration des avis
              </h1>
              <p className="text-gray-600">{stats?.company || domain}</p>
            </div>
            <button
              onClick={openSeoModal}
              className="flex items-center gap-2 px-4 py-2 border-2 border-[#00b67a] text-[#00b67a] rounded-lg hover:bg-[#00b67a] hover:text-white transition-colors text-sm font-medium"
              data-testid="seo-product-code-button"
            >
              <Code2 className="w-4 h-4" />
              Copier le code SEO Product
            </button>
          </div>
          
          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.review_counts?.approved || 0}</p>
                <p className="text-sm text-green-700">Approuvés</p>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.review_counts?.pending_moderation || 0}</p>
                <p className="text-sm text-yellow-700">En attente</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.review_counts?.pending_verification || 0}</p>
                <p className="text-sm text-blue-700">Non vérifiés</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.review_counts?.rejected || 0}</p>
                <p className="text-sm text-red-700">Rejetés</p>
              </div>
            </div>
          )}
        </div>

        {/* Pending Reviews */}
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-[#1a1a1a] flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              Avis en attente de modération
            </h2>
          </div>
          
          {pendingReviews.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Aucun avis en attente de modération</p>
              <p className="text-sm mt-2">Les avis apparaîtront ici une fois que les clients auront vérifié leur email.</p>
            </div>
          ) : (
            <div className="divide-y">
              {pendingReviews.map((review) => (
                <div key={review.id} className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {renderStars(review.rating)}
                        <span className="text-sm text-gray-500">
                          {new Date(review.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-1">{review.title}</h3>
                      <p className="text-gray-600 text-sm mb-2">{review.content}</p>
                      <p className="text-xs text-gray-400">Par {review.author}</p>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleModerate(review.id, 'approve')}
                        disabled={actionLoading === review.id}
                        className="flex items-center gap-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors"
                        data-testid={`approve-${review.id}`}
                      >
                        <Check className="w-4 h-4" />
                        Approuver
                      </button>
                      <button
                        onClick={() => handleModerate(review.id, 'reject')}
                        disabled={actionLoading === review.id}
                        className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
                        data-testid={`reject-${review.id}`}
                      >
                        <X className="w-4 h-4" />
                        Rejeter
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI-Generated Reviews */}
        <div className="bg-white rounded-lg border mt-6" data-testid="ai-reviews-section">
          <div className="p-4 border-b flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-semibold text-[#1a1a1a] flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#00b67a]" />
              Avis générés par IA
              <span className="text-sm font-normal text-gray-500">({aiTotal})</span>
            </h2>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={openBulkModal}
                className="flex items-center gap-2 px-4 py-2 border-2 border-[#00b67a] text-[#00b67a] rounded-lg hover:bg-[#00b67a] hover:text-white transition-colors text-sm font-medium"
                data-testid="bulk-generate-button"
              >
                <Layers className="w-4 h-4" />
                Génération en masse
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex items-center gap-2 px-4 py-2 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] disabled:opacity-50 transition-colors text-sm font-medium"
                data-testid="generate-ai-reviews-button"
              >
                <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
                {generating ? 'Génération...' : 'Générer des avis maintenant'}
              </button>
            </div>
          </div>

          <div className="px-4 py-3 bg-blue-50 border-b text-sm text-blue-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            De nouveaux avis sont générés automatiquement chaque jour (1 à 5 par entreprise) et affichés dans votre widget. Vous pouvez les modifier ou les supprimer ici.
          </div>

          {aiReviews.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Aucun avis IA pour le moment</p>
              <p className="text-sm mt-2">Cliquez sur "Générer des avis maintenant" ou attendez la génération quotidienne.</p>
            </div>
          ) : (
            <div className="divide-y">
              {aiReviews.map((review) => (
                <div key={review.id} className="p-4" data-testid={`ai-review-${review.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {renderStars(review.rating)}
                        <span className="text-sm text-gray-500">
                          {review.date}
                        </span>
                      </div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-1">{review.title}</h3>
                      <p className="text-gray-600 text-sm mb-2">{review.content}</p>
                      <p className="text-xs text-gray-400">Par {review.author}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEdit(review)}
                        className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        data-testid={`edit-${review.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDeleteReview(review.id)}
                        disabled={actionLoading === review.id}
                        className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition-colors"
                        data-testid={`delete-${review.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                        Supprimer
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      {editingReview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-review-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-bold text-[#1a1a1a] mb-4">Modifier l'avis</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Auteur</label>
                <input
                  type="text"
                  value={editForm.author}
                  onChange={(e) => setEditForm({ ...editForm, author: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
                  data-testid="edit-author-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setEditForm({ ...editForm, rating: n })}
                      className={`w-9 h-9 flex items-center justify-center rounded ${n <= editForm.rating ? 'bg-[#00b67a]' : 'bg-gray-200'}`}
                      data-testid={`edit-rating-${n}`}
                    >
                      <Star className="w-4 h-4 text-white fill-white" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
                  data-testid="edit-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contenu</label>
                <textarea
                  value={editForm.content}
                  onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                  rows={4}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
                  data-testid="edit-content-input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setEditingReview(null)}
                className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                data-testid="edit-cancel-button"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="px-4 py-2 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] disabled:opacity-50 transition-colors"
                data-testid="edit-save-button"
              >
                {savingEdit ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SEO Product Code Modal */}
      {showSeoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="seo-product-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
                  <Code2 className="w-5 h-5 text-[#00b67a]" />
                  Code SEO Product — {stats?.company || domain}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Ces codes rendent les étoiles <strong>éligibles</strong> sur le site du client (schéma Product). Google reste décideur de l'affichage.
                </p>
              </div>
              <button
                onClick={() => setShowSeoModal(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="seo-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Widget type selector */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
              <p className="text-xs font-medium text-gray-600 mb-2">Type de widget affiché</p>
              <div className="flex gap-2 flex-wrap" data-testid="seo-widget-type-selector">
                {['badge', 'banner', 'combo'].map((t) => (
                  <button
                    key={t}
                    onClick={() => setSeoWidgetType(t)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${seoWidgetType === t ? 'bg-[#00b67a] text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                    data-testid={`seo-widget-type-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Live preview */}
            <div className="mb-4">
              <p className="text-xs font-medium text-gray-600 mb-2">Aperçu en direct</p>
              <div className="border rounded-lg bg-gray-50 p-3 flex justify-center overflow-hidden" data-testid="seo-widget-preview">
                <iframe
                  key={seoWidgetType}
                  title="Aperçu du widget"
                  src={`${API_URL}/api/widget/embed.html?type=${seoWidgetType}&domain=${domain}&schema=product`}
                  className="w-full"
                  style={{ height: seoWidgetType === 'combo' ? '340px' : seoWidgetType === 'banner' ? '150px' : '190px', border: 'none' }}
                />
              </div>
            </div>

            {/* Product name editor */}
            <div className="mb-5 p-3 bg-green-50 rounded-lg border border-green-100">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Nom de la prestation (utilisé dans le schéma Product)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Ex : Toile de store sur mesure"
                  className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
                  data-testid="product-name-input"
                />
                <button
                  onClick={handleSaveProductName}
                  disabled={savingProduct || !productName.trim()}
                  className="flex-shrink-0 flex items-center gap-1 px-4 py-2 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] disabled:opacity-50 transition-colors text-sm font-medium"
                  data-testid="save-product-name-button"
                >
                  {productSaved ? <Check className="w-4 h-4" /> : null}
                  {savingProduct ? 'Enregistrement...' : productSaved ? 'Enregistré !' : 'Enregistrer'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Ce nom apparaît comme titre du produit noté dans les résultats Google.</p>
            </div>

            {/* Option A - Dynamic */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="inline-block text-xs font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded mb-1">RECOMMANDÉ</span>
                  <p className="text-sm font-medium text-[#1a1a1a]">Option A — Widget dynamique (mise à jour automatique)</p>
                  <p className="text-xs text-gray-500">À coller dans le <code>&lt;body&gt;</code>. Les nouveaux avis générés chaque jour sont pris en compte automatiquement.</p>
                </div>
                <button
                  onClick={() => copyText(dynamicSnippet, 'dynamic')}
                  className="flex-shrink-0 flex items-center gap-1 px-3 py-2 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] transition-colors text-sm font-medium"
                  data-testid="copy-dynamic-button"
                >
                  {copied === 'dynamic' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === 'dynamic' ? 'Copié !' : 'Copier'}
                </button>
              </div>
              <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap" data-testid="dynamic-snippet-code">{dynamicSnippet}</pre>
            </div>

            {/* Option B - Static */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-[#1a1a1a]">Option B — Snippet JSON-LD statique</p>
                  <p className="text-xs text-gray-500">À coller dans le <code>&lt;head&gt;</code>. À régénérer de temps en temps pour refléter les nouveaux avis.</p>
                </div>
                <button
                  onClick={() => copyText(seoSnippet, 'static')}
                  disabled={seoLoading || !seoSnippet}
                  className="flex-shrink-0 flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors text-sm font-medium"
                  data-testid="copy-static-button"
                >
                  {copied === 'static' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied === 'static' ? 'Copié !' : 'Copier'}
                </button>
              </div>
              <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-x-auto whitespace-pre-wrap max-h-64" data-testid="static-snippet-code">{seoLoading ? 'Chargement...' : seoSnippet}</pre>
            </div>

            <div className="mt-6 flex justify-end">
              <a
                href="https://search.google.com/test/rich-results"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#00b67a] hover:underline"
              >
                Valider avec le Rich Results Test de Google ↗
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Generate Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bulk-generate-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
                  <Layers className="w-5 h-5 text-[#00b67a]" />
                  Génération en masse — {stats?.company || domain}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Total actuel : <strong>{bulkCurrent}</strong> avis. Les nouveaux avis sont datés de façon étalée et publiés automatiquement.
                </p>
              </div>
              <button
                onClick={() => setShowBulkModal(false)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="bulk-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {bulkJob?.status === 'running' ? (
              <div data-testid="bulk-progress">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600">Génération en cours…</span>
                  <span className="font-medium text-[#1a1a1a]">
                    {bulkJob.created || 0} / {bulkJob.to_create || 0}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-[#00b67a] h-3 rounded-full transition-all duration-500"
                    style={{ width: `${bulkJob.to_create ? Math.min(100, Math.round(((bulkJob.created || 0) / bulkJob.to_create) * 100)) : 0}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Total visé : {bulkJob.target}. Tu peux fermer cette fenêtre, la génération continue côté serveur.
                </p>
              </div>
            ) : bulkJob?.status === 'done' ? (
              <div className="text-center py-4" data-testid="bulk-done">
                <Check className="w-10 h-10 text-[#00b67a] mx-auto mb-2" />
                <p className="font-medium text-[#1a1a1a]">Terminé !</p>
                <p className="text-sm text-gray-600 mt-1">Total actuel : <strong>{bulkJob.current_total}</strong> avis.</p>
                <button
                  onClick={() => { setBulkJob(null); openBulkModal(); }}
                  className="mt-4 text-sm text-[#00b67a] hover:underline"
                  data-testid="bulk-again-button"
                >
                  Lancer une nouvelle génération
                </button>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre total d'avis souhaité
                </label>
                <input
                  type="number"
                  value={bulkTarget}
                  onChange={(e) => setBulkTarget(e.target.value)}
                  min={bulkCurrent + 1}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00b67a]"
                  data-testid="bulk-target-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {bulkTarget && parseInt(bulkTarget, 10) > bulkCurrent
                    ? `${parseInt(bulkTarget, 10) - bulkCurrent} nouveaux avis seront générés.`
                    : 'Doit être supérieur au total actuel.'}
                </p>
                {bulkError && (
                  <p className="text-sm text-red-600 mt-2" data-testid="bulk-error">{bulkError}</p>
                )}
                {bulkJob?.status === 'error' && !bulkError && (
                  <p className="text-sm text-red-600 mt-2">Dernière tentative en erreur : {bulkJob.error}</p>
                )}
                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => setShowBulkModal(false)}
                    className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    data-testid="bulk-cancel-button"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={startBulk}
                    disabled={bulkStarting}
                    className="flex items-center gap-2 px-4 py-2 bg-[#00b67a] text-white rounded-lg hover:bg-[#00a36c] disabled:opacity-50 transition-colors font-medium"
                    data-testid="bulk-start-button"
                  >
                    <Layers className="w-4 h-4" />
                    {bulkStarting ? 'Démarrage...' : 'Lancer la génération'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminReviewsPage;
